import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ChatApp from './components/ChatApp';
import { AccessibilityProvider } from './context/AccessibilityContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import './i18n'; // Initialize i18n
import './embed.css'; // Embed-specific styles

const App: React.FC = () => {
  // Read configuration from embed script
  const embedConfig = (window as any).chatWidgetConfig || {};
  
  const [mode, setMode] = useState<'head' | 'popup' | 'fullscreen'>(
    embedConfig.initialMode || 'popup'
  );
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>(
    embedConfig.theme || 'light'
  );
  const [isI18nReady, setIsI18nReady] = useState(false);
  const { ready } = useTranslation();

  // Wait for i18n to be fully initialized
  useEffect(() => {
    const checkI18nReady = () => {
      if (ready) {
        setIsI18nReady(true);
      }
    };

    // Check immediately
    checkI18nReady();

    // Small delay to ensure everything is loaded
    const timer = setTimeout(checkI18nReady, 500);
    return () => clearTimeout(timer);
  }, [ready]);

  const handleModeChange = (newMode: 'head' | 'popup' | 'fullscreen') => {
    setMode(newMode);
  };

  // Show minimal loading state while translations are loading
  if (!isI18nReady) {
    return (
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        right: '20px', 
        width: '60px', 
        height: '60px', 
        borderRadius: '50%', 
        background: '#007bff', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        zIndex: 9999
      }}>
        ...
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        {/* No app wrapper - just the chat widget */}
        <ChatApp
          mode={mode}
          theme={theme}
          showThemeToggle={false}
          showLanguageSelector={false}
          onModeChange={handleModeChange}
          sseEndpoint={embedConfig.sseEndpoint || "http://localhost:3001/api/chat/stream"}
          introContent={embedConfig.introContent || {
            title: 'Chat Assistant',
            description: 'Hi! How can I help you today?',
            features: [
              'Real-time messaging',
              'Voice input support',
              'Smart responses',
              'Always available'
            ]
          }}
        />
      </AccessibilityProvider>
    </ErrorBoundary>
  );
};

export default App;
